import { useTranslation } from "@repo/i18n";
import { Skeleton } from "@repo/components";
import { EmptyState } from "@repo/blocks";
import { css } from "@repo/styled-system/css";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { GuestDetailPanel } from "./GuestDetailPanel";
import { guestsApi, guestsKeys, guestDisplayName, type GuestListItem } from "./guestsApi";

// Shared card + table styling, matching the polished sibling pages (waitlist / bookings ListView).
const card = css({
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "lg",
  bg: "bg.default",
  overflow: "hidden",
});
const cardHead = css({
  display: "flex",
  alignItems: "center",
  gap: "3",
  px: "4",
  py: "3",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
  bg: "bg.subtle",
});
const control = css({
  flex: "1",
  minW: "0",
  maxW: "md",
  minH: "40px",
  px: "3",
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "md",
  bg: "bg.default",
  fontSize: "sm",
  _focusVisible: { outlineWidth: "2px", outlineStyle: "solid", outlineColor: "border.emphasized" },
});
const scroll = css({ overflowX: "auto", width: "full" });
const table = css({ width: "full", borderCollapse: "collapse", fontSize: "sm" });
const th = css({
  textAlign: "left",
  px: "4",
  py: "2.5",
  fontWeight: "medium",
  color: "fg.muted",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
  whiteSpace: "nowrap",
});
const td = css({
  px: "4",
  py: "3",
  verticalAlign: "middle",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
});
const nameBtn = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1.5",
  fontWeight: "medium",
  color: "colorPalette.text",
  textAlign: "left",
  cursor: "pointer",
  _hover: { textDecoration: "underline" },
});
const chevron = css({
  display: "inline-block",
  width: "3",
  color: "fg.muted",
  fontSize: "2xs",
  transition: "transform 0.12s",
});

// Token-driven badges (no inline hex): blacklist = red no-show scale, id-check = amber pending scale.
const badgeBase = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  px: "2",
  py: "0.5",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "2xs",
  fontWeight: "medium",
  whiteSpace: "nowrap",
});
const blacklistBadge = css({
  bg: "booking.noShow.bg",
  color: "booking.noShow.fg",
  borderColor: "booking.noShow.border",
});
const idCheckBadge = css({
  bg: "booking.pending.bg",
  color: "booking.pending.fg",
  borderColor: "booking.pending.border",
});
const tagBadge = css({ bg: "bg.subtle", color: "fg.muted", borderColor: "border.default" });

/**
 * The `/v/:venueId/guests` directory (features/guest-crm-blacklist.md, §C.8). Cursor-paginated
 * list/search (newest first, ADR-0022) over the per-venue directory. Counters render inline as a
 * trust signal; the blacklist badge is a STAFF-only signal (a diner never learns their status, GU-4).
 * Clicking a client EXPANDS a detail row inline (visits + notes) directly beneath it. WAITER-gated.
 * Queries key under `["venue", venueId, "guests"]`, so `guest.*` / `blacklist.*` events invalidate them.
 */
export function GuestDirectoryPage() {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: guestsKeys.list(venueId, search),
    queryFn: () => guestsApi.list(venueId, search || undefined),
  });
  const items = query.data?.items ?? [];

  return (
    <PageScaffold title={t("guests.directory.title")}>
      <section className={card}>
        <div className={cardHead}>
          <label className={css({ display: "contents" })}>
            <span className={css({ srOnly: true })}>{t("guests.directory.searchLabel")}</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("guests.directory.searchPlaceholder")}
              className={control}
            />
          </label>
        </div>

        {query.isPending && (
          <div className={css({ p: "4" })} data-testid="guests-loading">
            <Skeleton height="8" mb="3" />
            <Skeleton height="6" mb="2" />
            <Skeleton height="6" />
          </div>
        )}
        {query.isError && (
          <div
            role="alert"
            data-testid="guests-error"
            className={css({ p: "4", color: "fg.error", fontSize: "sm" })}
          >
            {t("guests.directory.loadError")}
          </div>
        )}
        {query.data && items.length === 0 && (
          <div data-testid="guests-empty">
            <EmptyState icon="👤" title={t("guests.directory.empty")} />
          </div>
        )}

        {items.length > 0 && (
          <div className={scroll}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>{t("guests.directory.colName")}</th>
                  <th className={th}>{t("guests.directory.colContact")}</th>
                  <th className={th}>{t("guests.directory.colVisits")}</th>
                  <th className={th}>{t("guests.directory.colTags")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((g) => {
                  const expanded = expandedId === g.guestId;
                  return (
                    <Fragment key={g.guestId}>
                      <GuestRow
                        guest={g}
                        expanded={expanded}
                        onToggle={() =>
                          setExpandedId((id) => (id === g.guestId ? null : g.guestId))
                        }
                      />
                      {expanded && (
                        <tr data-testid="guest-detail-row">
                          <td
                            colSpan={4}
                            className={css({
                              p: "0",
                              borderBottomWidth: "1px",
                              borderColor: "border.subtle",
                              bg: "bg.subtle",
                            })}
                          >
                            <GuestDetailPanel venueId={venueId} guest={g} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PageScaffold>
  );
}

function GuestRow({
  guest,
  expanded,
  onToggle,
}: {
  guest: GuestListItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <tr
      data-testid="guest-row"
      data-expanded={expanded}
      className={css({ bg: expanded ? "bg.subtle" : "transparent" })}
    >
      <td className={td}>
        <button type="button" className={nameBtn} onClick={onToggle} aria-expanded={expanded}>
          <span aria-hidden className={chevron}>
            {expanded ? "▼" : "▶"}
          </span>
          {guestDisplayName(guest.givenName, guest.familyName)}
        </button>
        <span className={css({ display: "inline-flex", gap: "1", ml: "2" })}>
          {guest.blacklisted && (
            <span className={`${badgeBase} ${blacklistBadge}`} data-testid="blacklist-badge">
              {t("guests.directory.blacklistedBadge")}
            </span>
          )}
          {guest.idCheckRequired && (
            <span className={`${badgeBase} ${idCheckBadge}`}>
              {t("guests.directory.idCheckBadge")}
            </span>
          )}
        </span>
      </td>
      <td className={td}>{guest.phone ?? guest.email ?? "—"}</td>
      <td className={td}>
        {t("guests.directory.visitsSummary", {
          completed: guest.completed,
          noShows: guest.noShows,
          cancellations: guest.guestCancellations,
        })}
      </td>
      <td className={td}>
        {guest.tags.length > 0 ? (
          <span className={css({ display: "inline-flex", gap: "1", flexWrap: "wrap" })}>
            {guest.tags.map((tag) => (
              <span key={tag} className={`${badgeBase} ${tagBadge}`}>
                {tag}
              </span>
            ))}
          </span>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}
